import { addStyles } from 'ethcss'
import { color, palette, IStyles, mediaCSS } from 'theme'

const styles: IStyles = {
    modal: {
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: color.bgAlphaDark500,
        zIndex: 10000,
    },
    modalInner: {
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: color.bgWhitePrimary,
        width: '960px',
        height: '90vh',
        borderRadius: 20,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',

        [mediaCSS.tab]: {
            width: '98%',
        },
    },
    modalContent: {
        padding: '24px 24px 16px',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,

        [mediaCSS.tab]: {
            padding: '16px 16px 12px',
        },
    },
    header: {
        position: 'relative',
        marginBottom: 16,
        paddingRight: 32,
    },
    title: {
        fontSize: 24,
        fontWeight: 700,
        lineHeight: '32px',
        color: color.fontDarkUltraHigh,
        marginBottom: 4,

        [mediaCSS.tab]: {
            fontSize: 18,
            lineHeight: '24px',
        },
    },
    text: {
        fontSize: 14,
        lineHeight: '20px',
        color: color.fontDarkHigh,
    },
    closeBtn: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: 24,
        height: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        border: 'none',
        background: 'transparent',
        padding: 0,
        color: color.iconDarkMedium,
    },
    changes: {
        borderRadius: 12,
        border: `1px solid ${color.borderDarkPrimary}`,
        backgroundColor: color.bgWhiteSecondary,
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    scrollableArea: {
        padding: '0 0 12px',
        overflow: 'auto',
        flex: 1,
        minHeight: 0,
        backgroundColor: color.bgWhitePrimary,
    },
    changesBlock: {
        paddingBottom: 4,
    },
    broadcastHeader: {
        padding: '10px 16px',
        color: color.fontDarkHigh,
        fontWeight: 600,
        fontSize: 14,
        lineHeight: '20px',
        backgroundColor: color.bgWhiteTertiary,
        position: 'sticky',
        top: 0,
        zIndex: 10,
    },
    changesBlockLine: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        padding: '3px 0',
        fontSize: 13,
        lineHeight: '18px',
        gap: 4,
    },
    changesBlockLineCaption: {
        color: color.fontDarkHigh,
    },
    changesBlockPrevValue: {
        color: color.fontDarkMedium,
        textDecoration: 'line-through',
    },
    arrowRight: {
        color: color.blue,
        margin: '0 2px',
    },
    changesBlockCurrentValue: {
        color: color.blue,
    },
    blockWrapper: {
        border: `1px solid ${color.borderDarkPrimary}`,
        borderRadius: 8,
        marginTop: 4,
        marginBottom: 4,
        padding: '8px 12px 8px 12px',
        backgroundColor: color.bgWhiteSecondary,
    },
    treeSection: {
        padding: '8px 16px 12px',
        backgroundColor: color.bgWhitePrimary,
    },
    treeItem: {
        position: 'relative',
        paddingLeft: 16,
        listStyle: 'none',

        '&:not(:last-child)::before': {
            content: '""',
            position: 'absolute',
            left: 2,
            top: 6,
            bottom: -11,
            width: 1,
            backgroundColor: palette.neutral[500],
            zIndex: 0,
        },
    },
    treeLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        minHeight: 22,
        fontSize: 14,
        lineHeight: '20px',
        fontWeight: 600,
        color: color.fontDarkUltraHigh,
        position: 'relative',
    },
    treeLabelSecondary: {
        fontWeight: 500,
        fontSize: 13,
        color: color.fontDarkHigh,
    },
    treeHook: {
        position: 'absolute',
        left: -14,
        top: -12,
        width: 6,
        height: 24,
        color: palette.neutral[500],
        display: 'block',
        pointerEvents: 'none',
        zIndex: 1,
        lineHeight: 0,

        '& svg': {
            display: 'block',
        },
    },
    treeBullet: {
        width: 5,
        height: 5,
        borderRadius: '50%',
        backgroundColor: color.fontDarkUltraHigh,
        flexShrink: 0,
        position: 'relative',
        zIndex: 2,
    },
    treeLabelText: {
        flex: 1,
        minWidth: 0,
    },
    treeChildren: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        marginTop: 6,
        marginLeft: 0,
        paddingLeft: 0,

        '& > *:not(:last-child)': {
            marginBottom: 10,
        },

        '&::before': {
            content: '""',
            position: 'absolute',
            left: 2,
            top: -12,
            height: 18,
            width: 1,
            backgroundColor: palette.neutral[500],
        },
    },
    treePages: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    },
    treeItemRoot: {
        position: 'relative',
        listStyle: 'none',
        paddingLeft: 0,

        '&::after': {
            content: '""',
            position: 'absolute',
            left: 2,
            top: 16,
            width: 1,
            height: 14,
            backgroundColor: palette.neutral[500],
        },
    },
    treeLeaf: {
        position: 'relative',
        padding: '2px 0 4px 16px',

        '&:not(:last-child)::before': {
            content: '""',
            position: 'absolute',
            left: 2,
            top: 0,
            bottom: -11,
            width: 1,
            backgroundColor: palette.neutral[500],
            zIndex: 0,
        },
    },
    cardTreeNode: {
        position: 'relative',
        listStyle: 'none',
    },
    treeLeafGroup: {
        position: 'relative',
        margin: '0 0 4px',
        paddingLeft: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,

        '&::before': {
            content: '""',
            position: 'absolute',
            left: 2,
            top: 0,
            bottom: 4,
            width: 1,
            backgroundColor: palette.neutral[500],
        },
    },
    dontShowAgain: {
        marginTop: 16,
        flexShrink: 0,
    },
    footerDivider: {
        height: 1,
        width: '100%',
        backgroundColor: color.borderDarkPrimary,
        flexShrink: 0,
    },
    footerActions: {
        display: 'flex',
        justifyContent: 'center',
        gap: 8,
        padding: '16px 24px 24px',
        flexShrink: 0,

        [mediaCSS.tab]: {
            padding: '12px 16px 16px',

            '& > *': {
                flex: 1,
            },
        },
    },
    button: {
        minWidth: 108,
        borderRadius: 8,
        backgroundColor: color.bgWhitePrimary,
        color: color.fontDarkUltraHigh,
        border: `1px solid ${color.borderDarkPrimary}`,
    },
    buttonSave: {
        minWidth: 108,
        borderRadius: 8,
        backgroundColor: color.fontDarkUltraHigh,
        color: color.fontWhiteUltraHigh,
        border: 'none',
    },
    areasPreview: {
        marginTop: 8,
        marginLeft: 16,
        paddingLeft: 8,
    },
    areas: {
        position: 'relative',
        width: 200,
        height: 150,
        backgroundColor: color.bgDarkTertiary,
        borderRadius: 8,
        overflow: 'hidden',
    },
    area: {
        position: 'absolute',
        boxSizing: 'border-box',
        boxShadow: `inset 0 0 0 1px ${color.fontWhiteUltraHigh}`,
    },
    deleted: {
        backgroundColor: color.critical,
    },
    created: {
        backgroundColor: color.blue,
    },
}

addStyles(styles)
export default styles
